import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import employerService, { Employer } from '../../services/employerService';
import subscriptionService from '../../services/subscriptionService';
import jobService, { Job } from '../../services/jobService';
import {
  BuildingOfficeIcon,
  UserGroupIcon,
  BriefcaseIcon,
  StarIcon,
  MapPinIcon,
  GlobeAltIcon,
  ChatBubbleLeftRightIcon,
  PaperAirplaneIcon,
  EyeIcon,
  CalendarIcon,
  CheckCircleIcon,
  XCircleIcon,
  LockClosedIcon,
  CurrencyDollarIcon,
  ClockIcon
} from '@heroicons/react/24/outline';

interface EmployerStats {
  jobsPosted: number;
  activeJobs: number;
  totalApplications: number;
  hiredCandidates: number;
  averageTimeToHire: number;
  responseRate: number;
}

const EmployerViewPage: React.FC = () => {
  const { employerId } = useParams<{ employerId: string }>();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  
  const [employer, setEmployer] = useState<Employer | null>(null);
  const [jobs, setJobs] = useState<Job[]>([]);
  const [stats, setStats] = useState<EmployerStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [showInterviewModal, setShowInterviewModal] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [interviewMessage, setInterviewMessage] = useState('');
  const [selectedJobId, setSelectedJobId] = useState<string>('');

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    
    checkPremiumStatus();
    if (employerId) {
      fetchEmployerData();
    }
  }, [employerId, currentUser]);

  const checkPremiumStatus = async () => {
    if (!currentUser?.uid) return;
    
    try {
      // Check if user has student-plus or higher subscription
      const response = await subscriptionService.checkFeatureAccess('detailedEmployerInsights');
      const hasAccess = response.success && response.data?.hasAccess;
      setIsPremium(hasAccess);
      
      if (!hasAccess) {
        // Show upgrade prompt
        toast.error('This feature requires a Student Plus subscription or higher');
      }
    } catch (error) {
      console.error('Error checking premium status:', error);
      setIsPremium(false);
    }
  };

  const fetchEmployerData = async () => {
    if (!employerId) return;
    
    try {
      setLoading(true);
      
      // Fetch employer basic info
      const employerData = await employerService.getEmployerById(employerId);
      setEmployer(employerData);
      
      // Fetch employer's active jobs directly
      const employerJobs = await jobService.getJobsByEmployer(employerId);
      setJobs(employerJobs);
      
      // Extract stats from nested Firebase data structure
      const jobPostingStats = employerData.jobPostingStats || {
        activeJobs: 0,
        totalJobs: 0,
        totalApplications: 0,
        hiredCandidates: 0,
        averageApplicationsPerJob: 0
      };
      const hiringStats = employerData.hiringStats || {
        averageTimeToHire: 21,
        successRate: 0,
        candidateRating: 0,
        repeatApplicants: 0,
        responseRate: 85
      };
      
      setStats({
        jobsPosted: jobPostingStats.totalJobs,
        activeJobs: jobPostingStats.activeJobs,
        totalApplications: jobPostingStats.totalApplications,
        hiredCandidates: jobPostingStats.hiredCandidates,
        averageTimeToHire: hiringStats.averageTimeToHire,
        responseRate: hiringStats.responseRate || 85
      });
      
    } catch (error) {
      console.error('Error fetching employer data:', error);
      toast.error('Failed to load employer information');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (!messageText.trim() || !employerId) return;
    
    try {
      // In real implementation, send via API
      toast.success('Message sent successfully!');
      setShowMessageModal(false);
      setMessageText('');
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
    }
  };

  const handleRequestInterview = async () => {
    if (!interviewMessage.trim() || !selectedJobId) return;
    
    try {
      // In real implementation, send via API
      toast.success('Interview request sent successfully!');
      setShowInterviewModal(false);
      setInterviewMessage('');
      setSelectedJobId('');
    } catch (error) {
      console.error('Error sending interview request:', error);
      toast.error('Failed to send interview request');
    }
  };

  const handleApplyToJob = async (jobId: string) => {
    try {
      // In real implementation, apply via API
      toast.success('Application submitted successfully!');
    } catch (error) {
      console.error('Error applying to job:', error);
      toast.error('Failed to submit application');
    }
  };

  if (!isPremium) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center">
          <LockClosedIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Premium Feature</h2>
          <p className="text-gray-600 mb-6">
            Detailed employer insights are available with Student Plus subscription or higher.
          </p>
          <button
            onClick={() => navigate('/subscription')}
            className="bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors"
          >
            Upgrade to Student Plus
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!employer) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Employer Not Found</h2>
          <p className="text-gray-600">The employer you're looking for could not be found.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-4">
              <div className="h-20 w-20 bg-indigo-100 rounded-lg flex items-center justify-center">
                {employer.logo ? (
                  <img src={employer.logo} alt={employer.companyName} className="h-20 w-20 rounded-lg object-cover" />
                ) : (
                  <BuildingOfficeIcon className="h-12 w-12 text-indigo-600" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-3xl font-bold text-gray-900">{employer.companyName}</h1>
                <p className="text-gray-600 mt-1">{employer.companyDescription}</p>
                <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                  <div className="flex items-center">
                    <MapPinIcon className="h-4 w-4 mr-1" />
                    {employer.location}
                  </div>
                  <div className="flex items-center">
                    <UserGroupIcon className="h-4 w-4 mr-1" />
                    {employer.companySize}
                  </div>
                  <div className="flex items-center">
                    <StarIcon className="h-4 w-4 mr-1" />
                    {employer.averageRating}/5 ({employer.totalReviews} reviews)
                  </div>
                </div>
                {employer.website && (
                  <div className="flex items-center mt-2">
                    <GlobeAltIcon className="h-4 w-4 mr-1 text-gray-400" />
                    <a href={employer.website} target="_blank" rel="noopener noreferrer" 
                       className="text-indigo-600 hover:text-indigo-800 text-sm">
                      {employer.website}
                    </a>
                  </div>
                )}
              </div>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowMessageModal(true)}
                className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
              >
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2" />
                Send Message
              </button>
              <button
                onClick={() => setShowInterviewModal(true)}
                className="flex items-center px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
              >
                <CalendarIcon className="h-4 w-4 mr-2" />
                Request Interview
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <BriefcaseIcon className="h-8 w-8 text-blue-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Active Jobs</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.activeJobs}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <UserGroupIcon className="h-8 w-8 text-green-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Applications</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalApplications}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <CheckCircleIcon className="h-8 w-8 text-purple-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Hired Candidates</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.hiredCandidates}</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Time to Hire</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.averageTimeToHire} days</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Active Jobs */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Open Positions</h2>
          <div className="space-y-4">
            {jobs.length === 0 ? (
              <p className="text-gray-500 text-center py-8">No active job postings</p>
            ) : (
              jobs.map((job) => (
                <div key={job.id} className="border border-gray-200 rounded-lg p-4 hover:border-indigo-300 transition-colors">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900">{job.title}</h3>
                      <div className="flex items-center space-x-4 mt-1 text-sm text-gray-500">
                        <div className="flex items-center">
                          <MapPinIcon className="h-4 w-4 mr-1" />
                          {job.location}
                        </div>
                        <div className="flex items-center">
                          <BriefcaseIcon className="h-4 w-4 mr-1" />
                          {job.jobType}
                        </div>
                        {job.salaryRange && (
                          <div className="flex items-center">
                            <CurrencyDollarIcon className="h-4 w-4 mr-1" />
                            ${job.salaryRange.min?.toLocaleString()} - ${job.salaryRange.max?.toLocaleString()}
                          </div>
                        )}
                      </div>
                      <p className="text-gray-600 mt-2 line-clamp-2">{job.description}</p>
                      <p className="text-xs text-gray-400 mt-2">
                        Posted {new Date(job.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                    <button
                      onClick={() => handleApplyToJob(job.id)}
                      className="ml-4 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 transition-colors flex items-center"
                    >
                      <PaperAirplaneIcon className="h-4 w-4 mr-1" />
                      Apply
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Message Modal */}
      {showMessageModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Send Message to {employer.companyName}</h3>
            <textarea
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              placeholder="Write your message here..."
              className="w-full h-32 border border-gray-300 rounded-md px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => setShowMessageModal(false)}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSendMessage}
                disabled={!messageText.trim()}
                className="flex-1 bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
              >
                Send Message
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Interview Request Modal */}
      {showInterviewModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Request Interview</h3>
            
            {/* Job Selection */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Position</label>
              <select
                value={selectedJobId}
                onChange={(e) => setSelectedJobId(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              >
                <option value="">Choose a position</option>
                {jobs.map((job) => (
                  <option key={job.id} value={job.id}>{job.title}</option>
                ))}
              </select>
            </div>
            
            <textarea
              value={interviewMessage}
              onChange={(e) => setInterviewMessage(e.target.value)}
              placeholder="Why are you interested in this position?"
              className="w-full h-32 border border-gray-300 rounded-md px-3 py-2 focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
            />
            <div className="flex space-x-3 mt-4">
              <button
                onClick={() => {
                  setShowInterviewModal(false);
                  setSelectedJobId('');
                  setInterviewMessage('');
                }}
                className="flex-1 bg-gray-200 text-gray-800 px-4 py-2 rounded-md hover:bg-gray-300 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleRequestInterview}
                disabled={!interviewMessage.trim() || !selectedJobId}
                className="flex-1 bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 disabled:opacity-50 transition-colors"
              >
                Send Request
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployerViewPage;